import { useEffect, useMemo, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { useSearchParams } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'

import {
  useConfirmContract,
  useContracts,
  useCurrentUser,
  useFamily,
  useRecommendations,
} from '../hooks/queries'
import { useSession } from '../state/session'
import RecommendationStack from '../components/RecommendationStack'
import UploadModal from '../components/UploadModal'
import LoginModal from '../components/LoginModal'
import ContractsSidebar from '../components/ContractsSidebar'
import PixiScene, {
  useCoverTransform,
  type CharacterCommand,
} from '../components/scene/PixiScene'
import {
  CHARACTER_HOME_X,
  SCAN_STAND_X,
  ZONE_LABEL,
  ZONE_ORDER,
  ZONE_RECTS,
  type UploadZoneKey,
  type ZoneKey,
} from '../components/scene/sceneLayout'
import { zoneStateFor, type ZoneCoverage } from '../components/scene/zoneState'

/**
 * HomeScene — the SRAB home: a pixel-art town street rendered with PixiJS.
 *
 * Visuals are owned by `<PixiScene>` (a fixed 480×270 canvas upscaled crisply);
 * everything interactive lives in this React layer:
 *
 *   • Coverage per zone is derived from `useContracts()` + `useRecommendations()`
 *     via `zoneStateFor` and pushed to the world, which picks the insured vs
 *     uninsured art (neglected/shiny house · burning/lit car · worn/tagged
 *     luggage · Reaper/Angel for the `life` zone).
 *   • A transparent `<button>` hotspot is positioned over each object using the
 *     same cover-transform as the canvas, so clicks/hover/focus stay aligned at
 *     any viewport size. Real buttons keep the scene keyboard-accessible and
 *     give Playwright stable `data-testid`s.
 *   • Clicking an upload zone (house / vehicle / travel / life) sends the main
 *     character walking to it to mime a phone scan, then opens the upload modal.
 *     Clicking the person opens the login modal.
 *   • Night by default (moon); clicking the house raises the sun, staying day
 *     once the house is engaged or insured.
 *   • A logged-in user chip sits bottom-left, on the recommendation bar.
 *
 * Demo affordances (URL params, used by Playwright + ad-hoc checks):
 *   ?demoLogout=1 · ?demoCar=1 · ?demoHouse=1 · ?demoTravel=1 · ?demoLife=1
 */

/** Hotspot zone → Playwright test id. */
const TEST_ID: Record<ZoneKey, string> = {
  house: 'scene-house',
  vehicle: 'scene-car',
  travel: 'scene-suitcases',
  life: 'scene-life',
  pet: 'scene-pet',
  person: 'scene-person',
}

/** Upload zone → the scanning scene state it enters. */
const SCAN_STATE = {
  house: 'scanning-house',
  vehicle: 'scanning-vehicle',
  travel: 'scanning-travel',
  life: 'scanning-life',
  pet: 'scanning-pet',
} as const

/**
 * How long the walk + per-mode animation runs before the upload modal slides
 * in: a plain `scan`, the `die` collapse (Reaper), the `fly` plane gag, or the
 * `douse` fire-truck gag (vehicle on fire). `douse` is bookkeeping-only — the
 * character still walks and scans; the truck animation runs in parallel.
 */
type CharMode = CharacterCommand['mode']
type GagMode = CharMode | 'douse'
const MODE_DELAY: Record<'scan' | 'die' | 'fly' | 'douse', number> = {
  scan: 1700,
  die: 2300,
  fly: 2900,
  douse: 2800,
}
const RECS_HOLD_MS = 2500
const IDLE_CHARACTER: CharacterCommand = { mode: 'idle', x: CHARACTER_HOME_X }

export default function HomeScene(): JSX.Element {
  const sceneState = useSession((s) => s.sceneState)
  const setSceneState = useSession((s) => s.setSceneState)
  const modalZone = useSession((s) => s.modalZone)
  const openModal = useSession((s) => s.openModal)
  const closeModal = useSession((s) => s.closeModal)
  const loginModalOpen = useSession((s) => s.loginModalOpen)
  const openLoginModal = useSession((s) => s.openLoginModal)
  const closeLoginModal = useSession((s) => s.closeLoginModal)

  const { data: contracts = [] } = useContracts()
  const { data: recs = [] } = useRecommendations()
  const familyQuery = useFamily()
  // `useFamily` swallows 401/404 → `null`, so `isLoggedIn` is true only for a
  // real Family object (pre-fetch/anon/error all read as logged-out).
  const isLoggedIn = !!familyQuery.data
  const currentUser = useCurrentUser()
  const confirmContract = useConfirmContract()
  const queryClient = useQueryClient()
  const [searchParams, setSearchParams] = useSearchParams()
  const timeoutsRef = useRef<number[]>([])

  // ─── Demo URL affordances (force visual states; not user-facing) ─────────
  useEffect(() => {
    const sp = searchParams
    let dirty = false
    const next = new URLSearchParams(sp)

    if (sp.get('demoLogout') === '1') {
      queryClient.cancelQueries({ queryKey: ['family', 'me'] })
      queryClient.setQueryData(['family', 'me'], null)
      next.delete('demoLogout')
      dirty = true
    }

    function pushFakeContract(
      type: 'vehicle_mtpl' | 'property_building' | 'travel' | 'life' | 'pet',
    ) {
      const id = `demo-${type}`
      const fake = {
        id,
        family_id: 'demo-family',
        insurer: 'Demo Pojišťovna',
        type,
        holder_member_id: 'demo-member',
        insured_member_ids: ['demo-member'],
        premium_czk: 0,
        valid_from: '2025-01-01',
        valid_to: '2026-01-01',
        status: 'confirmed' as const,
        created_at: new Date().toISOString(),
      }
      queryClient.cancelQueries({ queryKey: ['contracts'] })
      const prev =
        (queryClient.getQueryData(['contracts']) as Array<typeof fake>) ?? []
      if (prev.some((c) => c.id === id)) return
      queryClient.setQueryData(['contracts'], [...prev, fake])
    }

    const demoFlags: Array<[string, Parameters<typeof pushFakeContract>[0]]> = [
      ['demoCar', 'vehicle_mtpl'],
      ['demoHouse', 'property_building'],
      ['demoTravel', 'travel'],
      ['demoLife', 'life'],
      ['demoPet', 'pet'],
    ]
    for (const [flag, type] of demoFlags) {
      if (sp.get(flag) === '1') {
        pushFakeContract(type)
        next.delete(flag)
        dirty = true
      }
    }

    if (dirty) setSearchParams(next, { replace: true })
  }, [searchParams, setSearchParams, queryClient])

  // Reset transient scene state on unmount; clear pending timeouts.
  useEffect(() => {
    const timeouts = timeoutsRef.current
    return () => {
      timeouts.forEach((t) => window.clearTimeout(t))
      timeoutsRef.current = []
      setSceneState('idle')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ─── Coverage per zone (drives the world's insured/uninsured art) ────────
  // `person` reflects auth, not contracts.
  const coverage = useMemo<Record<ZoneKey, ZoneCoverage>>(
    () => ({
      house: zoneStateFor('house', recs, contracts),
      vehicle: zoneStateFor('vehicle', recs, contracts),
      travel: zoneStateFor('travel', recs, contracts),
      life: zoneStateFor('life', recs, contracts),
      pet: zoneStateFor('pet', recs, contracts),
      person: isLoggedIn ? 'covered' : 'partial',
    }),
    [contracts, recs, isLoggedIn],
  )

  // Number of insured zones (0..5) — drives the character's accessory ladder
  // (sunglasses → top hat → gold chain → briefcase → sparkles).
  const characterLevel = useMemo(() => {
    const zones: ZoneKey[] = ['house', 'vehicle', 'travel', 'life', 'pet']
    return zones.filter((z) => coverage[z] !== 'missing').length
  }, [coverage])

  const [hovered, setHovered] = useState<ZoneKey | null>(null)
  const [character, setCharacter] = useState<CharacterCommand>(IDLE_CHARACTER)
  const [activeZone, setActiveZone] = useState<ZoneKey | null>(null)
  // Bumped each time the burning car is clicked → summons the fire-brigade truck.
  const [fireTruckTick, setFireTruckTick] = useState(0)
  // Bumped on every modal close/confirm so a parked truck drives off-screen.
  const [fireTruckLeaveTick, setFireTruckLeaveTick] = useState(0)

  // Day starts at night; the house click raises the sun, and it stays day once
  // the house is engaged or already insured.
  const [sunRaised, setSunRaised] = useState(false)
  const daytime = sunRaised || coverage.house !== 'missing'

  // Once the uninsured suitcases have been flown off, the spot stays empty
  // (no suitcases, no plane) until a travel contract is uploaded.
  const [travelFlownAway, setTravelFlownAway] = useState(false)
  const travelGone = coverage.travel === 'missing' && travelFlownAway

  // ─── Layout: cover-transform shared by canvas + hotspots ─────────────────
  const sceneRef = useRef<HTMLElement>(null)
  const transform = useCoverTransform(sceneRef)

  // ─── Interaction ─────────────────────────────────────────────────────────
  function pushTimeout(fn: () => void, ms: number) {
    timeoutsRef.current.push(window.setTimeout(fn, ms))
  }

  function handleZoneClick(zone: ZoneKey) {
    // Ignore while the character is busy (walking/scanning/dying) or a modal is open.
    if (character.mode !== 'idle' || modalZone !== null || loginModalOpen) return
    if (zone === 'person') {
      if (!isLoggedIn) {
        openLoginModal()
        return
      }
      // Logged in: the character waves at the viewer, then returns to idle.
      setCharacter({ mode: 'wave', x: CHARACTER_HOME_X })
      pushTimeout(() => setCharacter(IDLE_CHARACTER), 1800)
      return
    }
    // house | vehicle | travel | life | pet — the upload zones (key === modal zone).
    const uz: UploadZoneKey = zone
    if (uz === 'house') setSunRaised(true) // sun rises on the house click
    setSceneState(SCAN_STATE[uz])
    // Per-zone choreography, but only the *uninsured* gaps play the gag:
    //   life   → the Reaper cuts the character down (then a long arm scans),
    //   travel → the character boards a plane that flies off tail-first,
    //   vehicle→ a fire brigade rolls in and douses the burning car,
    //   else   → a plain standing phone scan.
    const mode: GagMode =
      uz === 'life' && coverage.life === 'missing'
        ? 'die'
        : uz === 'travel' && coverage.travel === 'missing' && !travelFlownAway
          ? 'fly'
          : uz === 'vehicle' && coverage.vehicle === 'missing'
            ? 'douse'
            : 'scan'
    setActiveZone(uz) // e.g. makes the cat hiss as the character approaches
    // 'douse' is the truck's gag; the character still walks over and scans.
    const charMode: CharMode = mode === 'douse' ? 'scan' : mode
    setCharacter({ mode: charMode, x: SCAN_STAND_X[uz] })
    if (mode === 'fly') {
      // The plane loads the suitcases mid-board → they vanish, and stay gone
      // until a travel contract is uploaded.
      pushTimeout(() => setTravelFlownAway(true), 1300)
    }
    if (mode === 'douse') setFireTruckTick((n) => n + 1)
    pushTimeout(() => openModal(uz), MODE_DELAY[mode])
  }

  function handleModalClose() {
    closeModal()
    setSceneState('idle')
    setCharacter(IDLE_CHARACTER) // walk back home
    setActiveZone(null)
    // Cancelled without uploading → the suitcases come back to their spot,
    // the sun goes back down (if it was raised for the house), and any parked
    // fire truck drives off (flames return to the still-uninsured car).
    setTravelFlownAway(false)
    setSunRaised(false)
    setFireTruckLeaveTick((n) => n + 1)
  }

  function handleModalConfirm(contractId: string) {
    // Optimistic: close + transition immediately; the confirm mutation runs in
    // the background and `useContracts` re-fetch flips the zone art.
    confirmContract.mutate(contractId)
    closeModal()
    setSceneState('recs-revealed')
    setCharacter(IDLE_CHARACTER)
    setActiveZone(null)
    setTravelFlownAway(false)
    setFireTruckLeaveTick((n) => n + 1) // truck departs after a successful upload too
    pushTimeout(() => setSceneState('idle'), RECS_HOLD_MS)
  }

  // ─── Render ────────────────────────────────────────────────────────────
  return (
    <main
      ref={sceneRef}
      data-testid="home-scene"
      data-state={sceneState}
      data-modal-zone={modalZone ?? 'none'}
      className="relative h-screen w-full overflow-hidden select-none"
      style={{ background: '#0b1b34' }}
    >
      {/* Pixel-art world (visuals only; pointer-events: none) */}
      <PixiScene
        coverage={coverage}
        hovered={hovered}
        transform={transform}
        daytime={daytime}
        character={character}
        activeZone={activeZone}
        travelGone={travelGone}
        fireTruckTick={fireTruckTick}
        fireTruckLeaveTick={fireTruckLeaveTick}
        characterLevel={characterLevel}
      />

      {/* Transparent hotspot layer — aligned to the canvas via the same
          cover-transform. Real buttons → a11y + Playwright targets. */}
      {ZONE_ORDER.map((zone) => {
        const r = ZONE_RECTS[zone]
        return (
          <button
            key={zone}
            type="button"
            data-testid={TEST_ID[zone]}
            data-zone={zone}
            data-state={coverage[zone]}
            aria-label={ZONE_LABEL[zone]}
            onClick={() => handleZoneClick(zone)}
            onPointerEnter={() => setHovered(zone)}
            onPointerLeave={() => setHovered((h) => (h === zone ? null : h))}
            onFocus={() => setHovered(zone)}
            onBlur={() => setHovered((h) => (h === zone ? null : h))}
            style={{
              position: 'absolute',
              left: transform.ox + r.x * transform.scale,
              top: transform.oy + r.y * transform.scale,
              width: r.w * transform.scale,
              height: r.h * transform.scale,
            }}
            className="z-20 cursor-pointer border-0 bg-transparent p-0 outline-none focus-visible:rounded focus-visible:ring-2 focus-visible:ring-pop-torch"
          />
        )
      })}

      {/* Logged-in user chip (bottom-left, on the recommendation bar) */}
      <AnimatePresence>
        {currentUser && (
          <motion.div
            key="user-chip"
            data-testid="home-user-chip"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 6 }}
            transition={{ duration: 0.25, ease: 'linear' }}
            className="pointer-events-none fixed bottom-0 left-0 z-[31] flex items-center pb-6 pl-4"
            title={currentUser.display_name}
          >
            <span className="max-w-[40vw] truncate border-[2px] border-pop-silhouette bg-pop-cream/85 px-2.5 py-1 text-xs font-semibold uppercase tracking-wide text-pop-silhouette backdrop-blur-sm">
              {currentUser.display_name}
            </span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ─── Contracts sidebar (left-edge slide-in, see #73) ───
          Mounted only when logged in. Reads `useContracts()` directly so
          loading/empty/error states are owned by the sidebar component. */}
      {isLoggedIn && <ContractsSidebar />}

      {/* Recommendation cards overlay */}
      <RecommendationStack />

      {/* Upload modal (house / vehicle / travel / life) */}
      <UploadModal
        zone={modalZone ?? 'house'}
        open={modalZone !== null}
        onClose={handleModalClose}
        onConfirm={(result) => handleModalConfirm(result.contract.id)}
      />

      {/* Login modal (person click while logged-out) */}
      <LoginModal
        open={loginModalOpen}
        onClose={closeLoginModal}
        onSuccess={() => closeLoginModal()}
      />
    </main>
  )
}
